
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ModuleContentEditor } from "../ModuleContentEditor";
import { ModuleDetailsEditor } from "../ModuleDetailsEditor";
import { toast } from "sonner"; // Import directly from sonner
import { useState } from "react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

type ModuleType = {
  id: string;
  module_number: number;
  part_number: number;
  title: string;
  content: string;
  duration_minutes: number;
  is_exam: boolean;
};

type ModulesListProps = {
  courseId: string;
  isEditing: boolean;
  onModuleChange: (moduleId: string, data: any) => void;
};

function ModuleRow({ module, onSelectModule }: { 
  module: ModuleType; 
  onSelectModule: (module: ModuleType) => void 
}) {
  return (
    <TableRow key={module.id}>
      <TableCell>{module.module_number}</TableCell>
      <TableCell>{module.part_number}</TableCell>
      <TableCell>{module.title}</TableCell>
      <TableCell>{module.duration_minutes}</TableCell>
      <TableCell>
        <Button 
          variant="link" 
          onClick={() => onSelectModule(module)}
          className="p-0 h-auto font-normal"
        >
          Edit Content
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function ModulesList({ courseId, isEditing, onModuleChange }: ModulesListProps) {
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminAuth();
  const [selectedModule, setSelectedModule] = useState<ModuleType | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const { data: modules, isLoading, error } = useQuery({
    queryKey: ['courseModules', courseId],
    queryFn: async () => {
      console.log('ModulesList: Fetching modules for course:', courseId);
      try {
        const { data, error } = await supabase
          .from('course_modules')
          .select('*')
          .eq('course_id', courseId)
          .order('module_number', { ascending: true })
          .order('part_number', { ascending: true });
        
        if (error) {
          console.error('Error fetching modules:', error);
          throw error;
        }
        
        console.log('ModulesList: Modules data:', data);
        return data as ModuleType[];
      } catch (error) {
        console.error('Failed to fetch modules:', error);
        throw error;
      }
    },
    retry: 2,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const handleDeleteModule = async (moduleId: string) => {
    if (deleteInProgress) return;
    
    setDeleteInProgress(true);
    try {
      console.log('Deleting module with ID:', moduleId);
      
      const { error, count } = await supabase
        .from('course_modules')
        .delete()
        .eq('id', moduleId);

      if (error) {
        console.error('Supabase error deleting module:', error);
        throw error;
      }

      console.log(`Successfully deleted ${count} module(s)`);
      toast.success('Module deleted successfully');
      
      await queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error('Failed to delete module');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleReload = () => {
    queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2">Loading modules...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <div className="text-destructive mb-4">Error loading modules: {(error as Error).message}</div>
        <Button variant="outline" onClick={handleReload}>Retry</Button>
      </div>
    );
  }

  if (!modules || modules.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted-foreground">No modules found for this course.</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Module</TableHead>
            <TableHead>Part</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Duration (min)</TableHead>
            {!isEditing && <TableHead>Content</TableHead>}
            {isEditing && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {modules?.map((module) => (
            isEditing ? (
              <ModuleDetailsEditor 
                key={module.id}
                module={module}
                courseId={courseId}
                onDelete={() => handleDeleteModule(module.id)}
                onChange={onModuleChange}
              />
            ) : (
              <ModuleRow
                key={module.id}
                module={module}
                onSelectModule={setSelectedModule}
              />
            )
          ))}
        </TableBody>
      </Table>

      {selectedModule && (
        <ModuleContentEditor
          isOpen={true}
          onClose={() => setSelectedModule(null)}
          moduleId={selectedModule.id}
          initialContent={selectedModule.content}
          moduleTitle={selectedModule.title}
          courseId={courseId}
        />
      )}
    </>
  );
}
